// ─── NEWS TAB — contextual news intelligence layer ────────────────────────────
// Dependencies: core (C, s, DataStatus), NewsService (newsService singleton)
// News here is signal context, not a headline firehose. Category filter feeds
// the main aggregate feed (all three APIs); the signal-linked section attaches
// news to whatever signals the Signal Board has active.
//
// Pushes to globalSignals via onData("newsSentiment", ...) after each fetch so
// the Signal Board can evaluate the news-sentiment rule.

import { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { C, s, DataStatus, backendFetch } from "./core";
import { newsService } from "./NewsService";

// ─── BACKEND NEWS FETCH ───────────────────────────────────────────────────────
// Routes through window.QUANT_SIGNAL_BACKEND when configured (adds auth +
// Content-Type). The direct URL is a dead fallback; the backend path is the
// real call. Backend returns a JSON array of pre-normalized article objects.
async function fetchNews(path) {
  const res = await backendFetch("https://newsdata.io", path);
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// ─── CATEGORY FILTER BAR ──────────────────────────────────────────────────────
const CATEGORIES = [
  { label: "ALL",          cat: "all"          },
  { label: "MARKETS",      cat: "markets"      },
  { label: "MACRO/FED",    cat: "macro"        },
  { label: "ENERGY",       cat: "energy"       },
  { label: "CRYPTO",       cat: "crypto"       },
  { label: "EARNINGS",     cat: "earnings"     },
  { label: "GEOPOLITICAL", cat: "geopolitical" },
];

// ─── REPRESENTATIVE FEED (shown when no keys configured) ──────────────────────
// Clearly labeled REPRESENTATIVE with a red DataStatus badge so it is never
// mistaken for live coverage.
const REPRESENTATIVE = [
  { id: "rep1", source: "Reuters",    ageLabel: "12 min ago", sentiment: "negative",
    title: "Treasury yields climb as markets price in higher-for-longer rate path",
    summary: "Two-year yields touched fresh highs after hawkish commentary...", url: "https://newsdata.io" },
  { id: "rep2", source: "Bloomberg",  ageLabel: "38 min ago", sentiment: "positive",
    title: "Energy majors rally on tighter crude inventories and OPEC discipline",
    summary: "A larger-than-expected draw lifted the complex broadly...", url: "https://gnews.io" },
  { id: "rep3", source: "AP",         ageLabel: "1 hr ago",   sentiment: "neutral",
    title: "Fed officials signal patience as inflation data comes in mixed",
    summary: "Policymakers reiterated a data-dependent stance heading into...", url: "https://currentsapi.services" },
  { id: "rep4", source: "CNBC",       ageLabel: "2 hr ago",   sentiment: "negative",
    title: "Regional bank shares slide on renewed commercial real estate fears",
    summary: "Concerns over office exposure weighed on the sector...", url: "https://newsdata.io" },
  { id: "rep5", source: "Reuters",    ageLabel: "3 hr ago",   sentiment: "positive",
    title: "Chipmakers extend gains on resilient data-center demand outlook",
    summary: "Guidance from suppliers pointed to sustained AI capex...", url: "https://gnews.io" },
  { id: "rep6", source: "Bloomberg",  ageLabel: "5 hr ago",   sentiment: "neutral",
    title: "Dollar steadies ahead of key payrolls print; gold holds bid",
    summary: "Traders positioned cautiously into the labor report...", url: "https://currentsapi.services" },
];

// ─── SENTIMENT BADGE ──────────────────────────────────────────────────────────
function SentimentBadge({ sentiment }) {
  const map = {
    positive: { bg: C.greenBg,  color: C.green,   text: "● POSITIVE" },
    negative: { bg: C.redBg,    color: "#FF5252", text: "● NEGATIVE" },
    neutral:  { bg: C.panelAlt, color: C.textDim, text: "● NEUTRAL"  },
  };
  const st = map[sentiment] || map.neutral;
  return (
    <span style={{
      background: st.bg, color: st.color, fontSize: 11, fontWeight: 700,
      padding: "1px 6px", borderRadius: 2, whiteSpace: "nowrap", flexShrink: 0,
    }}>
      {st.text}
    </span>
  );
}

// ─── ARTICLE CARD (full width) ────────────────────────────────────────────────
function ArticleCard({ a }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: "10px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 11, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ color: C.textMid, fontWeight: 700 }}>{a.source}</span>
          <span style={{ color: C.textDim }}> · {a.ageLabel}</span>
        </div>
        <SentimentBadge sentiment={a.sentiment} />
      </div>
      <div style={{
        color: C.text, fontWeight: 700, fontSize: 13, lineHeight: 1.4,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflow: "hidden", marginBottom: 4,
      }}>
        {a.title}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.textDim, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {a.summary}
        </span>
        {a.url && (
          <a href={a.url} target="_blank" rel="noopener noreferrer"
            style={{ color: C.blue, fontSize: 11, whiteSpace: "nowrap", textDecoration: "none", flexShrink: 0 }}>
            Read more ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ─── COMPACT CARD (signal-linked — headline + source + age, no summary) ───────
function CompactCard({ a }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: "7px 0" }}>
      <div style={{
        color: C.text, fontWeight: 700, fontSize: 13, lineHeight: 1.4,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflow: "hidden", marginBottom: 3,
      }}>
        {a.url
          ? <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: C.text, textDecoration: "none" }}>{a.title}</a>
          : a.title}
      </div>
      <div style={{ fontSize: 11 }}>
        <span style={{ color: C.textMid, fontWeight: 700 }}>{a.source}</span>
        <span style={{ color: C.textDim }}> · {a.ageLabel}</span>
      </div>
    </div>
  );
}

// ─── SETUP INSTRUCTIONS (no keys) ─────────────────────────────────────────────
function NewsSetup() {
  return (
    <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.8, marginBottom: 10 }}>
      Live news is unavailable — the feed below is <span style={{ color: "#FF5252", fontWeight: 700 }}>REPRESENTATIVE</span>, not live.
    </div>
  );
}

// ─── NEWS TAB ─────────────────────────────────────────────────────────────────
export function NewsTab({ keys, onData, globalSignals }) {
  const [articles,   setArticles]   = useState([]);
  const [category,   setCategory]   = useState("all");
  const [loading,    setLoading]    = useState(false);
  const [lastFetch,  setLastFetch]  = useState(null);
  const [signalNews, setSignalNews] = useState({});
  const [statusMap,  setStatusMap]  = useState("offline");

  const hasKeys = !!(window.QUANT_SIGNAL_BACKEND || keys?.newsdataKey || keys?.gnewsKey || keys?.currentsKey);

  // ── Fetch main feed for a category ──
  const load = useCallback(async (cat) => {
    if (!hasKeys) { setArticles([]); setStatusMap("mock"); return; }
    setLoading(true);
    const arts = await fetchNews(`/api/news?category=${cat}`);
    setArticles(arts);
    setStatusMap(arts.length ? "live" : "offline");
    setLastFetch(new Date().toLocaleTimeString());
    setLoading(false);
    if (onData) onData("newsSentiment", newsService.computeOverallSentiment(arts));
  }, [keys, hasKeys, onData]);

  // ── Load on mount + category change ──
  useEffect(() => { load(category); }, [category, load]);

  // ── Auto-refresh every 15 minutes ──
  useEffect(() => {
    if (!hasKeys) return;
    const t = setInterval(() => load(category), 15 * 60 * 1000);
    return () => clearInterval(t);
  }, [category, load, hasKeys]);

  // ── Active signals from the Signal Board ──
  const activeSignals = globalSignals?.activeSignals || [];
  const signalTypes = [...new Set(activeSignals.map((sig) => sig.type))];
  const signalKey = signalTypes.join("|");

  // ── Lazily fetch signal-linked news when signals are active ──
  useEffect(() => {
    if (!hasKeys || signalTypes.length === 0) return;
    let cancelled = false;
    (async () => {
      const map = {};
      for (const type of signalTypes) {
        const arts = await fetchNews(`/api/news/signal/${encodeURIComponent(type)}`);
        if (cancelled) return;
        map[type] = arts;
      }
      if (!cancelled) setSignalNews(map);
    })();
    return () => { cancelled = true; };
    // signalKey captures the set of active types; keys re-triggers on key change.
  }, [signalKey, hasKeys, keys]); // eslint-disable-line react-hooks/exhaustive-deps

  const feed = hasKeys ? articles : REPRESENTATIVE;

  return (
    <div>
      {/* ── SECTION 1 — Category filter bar ── */}
      <div style={{ display: "flex", gap: 5, marginBottom: 10, flexWrap: "wrap" }}>
        {CATEGORIES.map((c) => (
          <button
            key={c.cat}
            style={{ ...s.btn(category === c.cat ? "amber" : "default"), fontSize: 11 }}
            onClick={() => setCategory(c.cat)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* ── SECTION 2 + 3 — Main feed ── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>FINANCIAL NEWS FEED</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <DataStatus
              status={hasKeys ? statusMap : "mock"}
              source={hasKeys ? "" : "representative"}
              timestamp={hasKeys ? lastFetch : null}
            />
            {hasKeys && (
              <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={() => load(category)} title="Refresh">
                <RefreshCw size={10} />
              </button>
            )}
          </div>
        </div>

        <div style={s.panelB}>
          {!hasKeys && <NewsSetup />}
          {loading ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: "6px 0" }}>FETCHING NEWS...</div>
          ) : feed.length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: "6px 0" }}>
              No articles for this category right now.
            </div>
          ) : (
            feed.slice(0, 20).map((a) => <ArticleCard key={a.id} a={a} />)
          )}
        </div>
      </div>

      {/* ── SECTION 4 — Signal-linked news ── */}
      {activeSignals.length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={{ ...s.panelT, color: C.amber }}>NEWS CONTEXT — ACTIVE SIGNALS</span>
            <span style={{ ...s.tag(), fontSize: 11 }}>{signalTypes.length} SIGNAL TYPE{signalTypes.length !== 1 ? "S" : ""}</span>
          </div>
          <div style={s.panelB}>
            {signalTypes.map((type) => {
              const sig = activeSignals.find((x) => x.type === type);
              const arts = signalNews[type] || [];
              return (
                <div key={type} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 15 }}>{sig?.icon || "◈"}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: sig?.color || C.text, letterSpacing: "0.06em" }}>
                      {sig?.title || type}
                    </span>
                    <span style={s.tag(sig?.color)}>{type}</span>
                  </div>
                  {!hasKeys ? (
                    <div style={{ fontSize: 11, color: C.textDim, paddingLeft: 23 }}>
                      Data unavailable.
                    </div>
                  ) : arts.length === 0 ? (
                    <div style={{ fontSize: 11, color: C.textDim, paddingLeft: 23 }}>Loading related news...</div>
                  ) : (
                    <div style={{ paddingLeft: 23 }}>
                      {arts.map((a) => <CompactCard key={a.id} a={a} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 4, lineHeight: 1.6 }}>
        Auto-refreshes every 15 minutes ·{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Sentiment is heuristic context, not a recommendation.</span>
      </div>
    </div>
  );
}
